/**
 * Result table for 訪問予定表(施術師毎) — /visits/massager_init.
 * One row per visit (not grouped). No row selection (print/CSV uses form state).
 * A2/R2: last two column titles toggle between はり・きゅう師 and マッサージ師.
 */

import { AppPagination, AppTable, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import type { HomeVisitMassagerPagination, HomeVisitMassagerRow } from '../types';

interface Props {
  data: HomeVisitMassagerRow[];
  pagination: HomeVisitMassagerPagination | null;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  hasSearched: boolean;
  serviceType: string;
}

const HomeVisitMassagerTable: React.FC<Props> = ({
  data,
  pagination,
  onPageChange,
  isLoading,
  hasSearched,
  serviceType,
}) => {
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';
  // Legacy RA2 header text — NOT derived from massagerLabel.
  // A2 uses 施術師NO (generic), R2 uses abbreviated マNo.
  const massagerNoLabel = isA2 ? '施術師NO' : 'マNo';

  const columns: ColumnsType<HomeVisitMassagerRow> = useMemo(
    () => [
      {
        title: '訪問日',
        dataIndex: 'visit_date',
        key: 'visit_date',
        width: 110,
      },
      {
        title: '曜日',
        dataIndex: 'day_of_week',
        key: 'day_of_week',
        width: 60,
      },
      {
        title: '訪問時間',
        dataIndex: 'visit_time',
        key: 'visit_time',
        width: 80,
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 130,
        render: (_: unknown, record: HomeVisitMassagerRow) => (
          <PatientEditLink
            patientId={record.patient_id}
            value={record.serial_number}
          />
        ),
      },
      {
        title: '受診者名',
        dataIndex: 'patient_name',
        key: 'patient_name',
        width: 180,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '訪問住所',
        dataIndex: 'patient_address',
        key: 'patient_address',
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: massagerNoLabel,
        dataIndex: 'massager_serial_number',
        key: 'massager_serial_number',
        width: 110,
      },
      {
        title: massagerLabel,
        dataIndex: 'massager_name',
        key: 'massager_name',
        width: 160,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
    ],
    [massagerLabel, massagerNoLabel]
  );

  // BD item 18: `{TOTAL} 件中 {START}件から{END}件 を表示しています`.
  // When there are 0 results: BD only requires the header plus empty message text -> DO NOT show info text.
  const infoText = useMemo(() => {
    if (!hasSearched || !pagination) return '';
    const total = pagination.total ?? 0;
    if (total <= 0) return '';
    return `${total} 件中 ${pagination.from ?? 0}件から${pagination.to ?? 0}件 を表示しています`;
  }, [hasSearched, pagination]);

  return (
    <div>
      <AppTable
        columns={columns}
        dataSource={data}
        rowKey="visit_id"
        loading={isLoading}
        pagination={false}
        hasFilters={hasSearched}
        tableLayout="fixed"
      />

      {infoText && (
        <div className="flex items-center justify-between gap-3 mt-2">
          {pagination && pagination.last_page > 1 && (
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={onPageChange}
              showSizeChanger={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HomeVisitMassagerTable;
